import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, Component, ViewEncapsulation, Input, Output, ViewChild, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

/**
 * Checkbox component which supports an indeterminate value
 */
const _c0 = ["inputElement"];
const _c1 = [[["", "slot", "image"]]];
const _c2 = ["[slot='image']"];
class CheckboxComponent {
  constructor(renderer) {
    this.renderer = renderer;
    /**
     * The value sent to the server when the checkbox is ticked
     */
    this.value = 'on';
    /**
     * Event handler for when the checkbbox is clicked
     */
    this.clicked = new EventEmitter();
    /**
     * Two way binding for indeterminate binding
     */
    this.indeterminateChange = new EventEmitter();
    this.innerValue = false;
    /**
     * The registered callback function called when a change event occurs on the input element.
     */
    this.onChange = _ => {}; // eslint-disable-line @typescript-eslint/no-unused-vars
    /**
     * The registered callback function called when a blur event occurs on the input element.
     */
    this.onTouched = _ => {}; // eslint-disable-line @typescript-eslint/no-unused-vars
  }
  static {
    this.uniqueId = 0;
  }
  /**
   * Write a value to the html element
   */
  writeValue(value) {
    this.innerValue = value;
    if (this.inputElement) {
      this.renderer.setProperty(this.inputElement.nativeElement, 'checked', value);
      this.renderer.setProperty(this.inputElement.nativeElement, 'aria-checked', value);
    }
  }
  /**
   * @description
   * Registers a function called when the control value changes.
   *
   * @param fn The callback function
   */
  registerOnChange(fn) {
    this.onChange = fn;
  }
  /**
   * @description
   * Registers a function called when the control is touched.
   *
   * @param fn The callback function
   */
  registerOnTouched(fn) {
    this.onTouched = fn;
  }
  /**
   * Invoked when the disabled attribute is updated
   */
  setDisabledState(isDisabled) {
    this.renderer.setProperty(this.inputElement.nativeElement, 'disabled', isDisabled);
    this.renderer.setProperty(this.inputElement.nativeElement, 'aria-disabled', isDisabled);
  }
  /**
   * Fired when the checkbox is called.  It's responsible for notifying registered calleers.
   */
  handleClick(event) {
    this.innerValue = event.target.checked;
    this.indeterminate = false;
    this.indeterminateChange.emit(this.indeterminate);
    this.clicked.emit(event);
  }
  setIndeterminate(value) {
    this.inputElement.nativeElement.indeterminate = value;
  }
  ngOnChanges(changes) {
    if (changes && changes.indeterminate && changes.indeterminate.currentValue !== undefined) {
      if (this.inputElement) {
        this.setIndeterminate(changes.indeterminate.currentValue);
      }
    }
    // If this is component is marked as read only we need to disable the real checkbox.
    // Otherwise the label is clickable and will change the value of the checkbox.
    if (changes && changes.readonly && changes.readonly.currentValue !== undefined) {
      if (this.inputElement) {
        this.setDisabledState(changes.readonly.currentValue);
      }
    }
  }
  ngAfterViewInit() {
    this.setIndeterminate(this.indeterminate);
    if (this.readonly) {
      this.setDisabledState(true);
    }
    this.renderer.setProperty(this.inputElement.nativeElement, 'readonly', this.readonly);
    this.renderer.setProperty(this.inputElement.nativeElement, 'aria-readonly', this.readonly);
    this.renderer.setProperty(this.inputElement.nativeElement, 'checked', this.innerValue);
    this.renderer.setProperty(this.inputElement.nativeElement, 'aria-checked', this.innerValue);
  }
  ngAfterContentChecked() {
    if (!this.checkboxId) {
      CheckboxComponent.uniqueId = CheckboxComponent.uniqueId + 1;
      this.checkboxId = 'checkbox-' + CheckboxComponent.uniqueId;
    }
  }
  /** @nocollapse */
  static {
    this.ɵfac = function CheckboxComponent_Factory(t) {
      return new (t || CheckboxComponent)(i0.ɵɵdirectiveInject(i0.Renderer2));
    };
  }
  /** @nocollapse */
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: CheckboxComponent,
      selectors: [["cxone-checkbox"]],
      viewQuery: function CheckboxComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c0, 7);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.inputElement = _t.first);
        }
      },
      inputs: {
        checkboxId: "checkboxId",
        disabled: "disabled",
        indeterminate: "indeterminate",
        labelText: "labelText",
        name: "name",
        ariaLabel: "ariaLabel",
        readonly: "readonly",
        value: "value"
      },
      outputs: {
        clicked: "clicked",
        indeterminateChange: "indeterminateChange"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => CheckboxComponent),
        multi: true
      }]), i0.ɵɵNgOnChangesFeature],
      ngContentSelectors: _c2,
      decls: 11,
      vars: 13,
      consts: [[1, "cxone-checkbox", "noselect"], ["type", "checkbox", 3, "id", "name", "value", "click", "change", "blur"], ["inputElement", ""], ["role", "checkbox", 3, "for"], [1, "icon"], [1, "text", 3, "id"], [1, "check-wrapper"], [1, "check"]],
      template: function CheckboxComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c1);
          i0.ɵɵelementStart(0, "div", 0)(1, "div")(2, "input", 1, 2);
          i0.ɵɵlistener("click", function CheckboxComponent_Template_input_click_2_listener($event) {
            return ctx.handleClick($event);
          })("change", function CheckboxComponent_Template_input_change_2_listener($event) {
            return ctx.onChange($event.target.checked);
          })("blur", function CheckboxComponent_Template_input_blur_2_listener($event) {
            return ctx.onTouched($event);
          });
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(4, "label", 3)(5, "span", 4);
          i0.ɵɵprojection(6);
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(7, "span", 5)(8, "div", 6);
          i0.ɵɵelement(9, "div", 7);
          i0.ɵɵelementEnd();
          i0.ɵɵtext(10);
          i0.ɵɵelementEnd()()()();
        }
        if (rf & 2) {
          i0.ɵɵattribute("disabled", ctx.disabled)("readonly", ctx.readonly);
          i0.ɵɵadvance(2);
          i0.ɵɵpropertyInterpolate("id", ctx.checkboxId);
          i0.ɵɵproperty("name", ctx.name)("value", ctx.value);
          i0.ɵɵattribute("aria-checked", ctx.innerValue)("aria-disabled", ctx.disabled)("aria-readonly", ctx.readonly)("aria-label", ctx.ariaLabel)("aria-labelledby", ctx.ariaLabel ? undefined : "label_" + ctx.checkboxId);
          i0.ɵɵadvance(2);
          i0.ɵɵpropertyInterpolate("for", ctx.checkboxId);
          i0.ɵɵadvance(3);
          i0.ɵɵpropertyInterpolate1("id", "label_", ctx.checkboxId, "");
          i0.ɵɵadvance(3);
          i0.ɵɵtextInterpolate1("", ctx.labelText, " ");
        }
      },
      styles: [".cxone-checkbox{position:relative;display:inline;line-height:24px}.cxone-checkbox.noselect{-webkit-touch-callout:none;-webkit-user-select:none;-khtml-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.cxone-checkbox .icon,.cxone-checkbox .text{display:inline-block}.cxone-checkbox .icon svg{vertical-align:middle}.cxone-checkbox label{vertical-align:middle;font-weight:400}.cxone-checkbox [slot=image]{margin-right:10px;width:20px;height:20px}.cxone-checkbox input[type=checkbox]{opacity:0;vertical-align:middle;position:absolute}.cxone-checkbox input[type=checkbox]:focus-visible+label:before{border:1px solid rgb(0,124,190)}.cxone-checkbox input[type=checkbox]+label{font-size:13px}.cxone-checkbox input[type=checkbox]:indeterminate+label:after{content:\"\";opacity:1;top:8px;left:5px;width:6px;height:6px;background:#007cbe;-webkit-transform:rotate(0deg);-moz-transform:rotate(0deg);-o-transform:rotate(0deg);-ms-transform:rotate(0deg);transform:rotate(0)}.cxone-checkbox input[type=checkbox]+label:after{content:none}.cxone-checkbox input[type=checkbox]:checked+label:after{content:\"\"}.cxone-checkbox[disabled=true] input+label{color:#adadad}.cxone-checkbox[disabled=true] input+label:before{background:#f8f8f8 0 0 no-repeat padding-box;border:1px solid #526B7A}.cxone-checkbox[disabled=true] input+label:hover:before{border:1px solid #526B7A}.cxone-checkbox[disabled=true] input+label:after{color:#526b7a;opacity:.8}.cxone-checkbox[readonly=true] .check-wrapper{display:inline-block;width:16px}.cxone-checkbox[readonly=true] input:checked+label{font-family:OpenSans;font-weight:600}.cxone-checkbox[readonly=true] input:checked+label .check{display:inline-block;position:relative;top:-2px;transform:rotate(45deg);height:10px;width:5px;border-bottom:1.5px solid rgb(0,124,190);border-right:1.5px solid rgb(0,124,190);margin-right:10px}.cxone-checkbox[readonly=true] input:not(checked)+label{font-family:OpenSans}.cxone-checkbox:not([readonly]) label{position:relative;display:inline-block;color:#2e2e2e;padding-left:26px}.cxone-checkbox:not([readonly]) label:before,.cxone-checkbox:not([readonly]) label:after{position:absolute;content:\"\";display:inline-block}.cxone-checkbox:not([readonly]) label:hover:before{border:1px solid rgb(0,124,190)}.cxone-checkbox:not([readonly]) label:before{height:16px;width:16px;background:#fff 0 0 no-repeat padding-box;border:1px solid #526B7A;opacity:.8;left:0;top:3px}.cxone-checkbox:not([readonly]) label:after{height:5px;width:10px;border-left:1.5px solid;border-bottom:1.5px solid;display:inline-block;transform:rotate(-45deg);color:#007cbe;left:3px;top:8px}\n"],
      encapsulation: 2
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CheckboxComponent, [{
    type: Component,
    args: [{
      selector: 'cxone-checkbox',
      providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => CheckboxComponent),
        multi: true
      }],
      encapsulation: ViewEncapsulation.None,
      template: "<div class=\"cxone-checkbox noselect\" [attr.disabled]=\"disabled\" [attr.readonly]=\"readonly\">\n    <div>\n        <input\n            #inputElement\n            id=\"{{checkboxId}}\"\n            [name]=\"name\"\n            type=\"checkbox\"\n            [value]=\"value\"\n            [attr.aria-checked]=\"innerValue\"\n            [attr.aria-disabled]=\"disabled\"\n            [attr.aria-readonly]=\"readonly\"\n            [attr.aria-label]=\"ariaLabel\"\n            [attr.aria-labelledby]=\"ariaLabel ? undefined : 'label_'+checkboxId\"\n            (click)=\"handleClick($event)\"\n            (change)=\"onChange($event.target.checked)\"\n            (blur)=\"onTouched($event)\"\n        >\n        <label role=\"checkbox\" for=\"{{checkboxId}}\">\n            <span class=\"icon\"><ng-content select=\"[slot='image']\"></ng-content></span><span class=\"text\"  id=\"label_{{checkboxId}}\">\n                <div class=\"check-wrapper\"><div class=\"check\"></div></div>{{labelText}}\n            </span>\n        </label>\n    </div>\n</div>\n",
      styles: [".cxone-checkbox{position:relative;display:inline;line-height:24px}.cxone-checkbox.noselect{-webkit-touch-callout:none;-webkit-user-select:none;-khtml-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.cxone-checkbox .icon,.cxone-checkbox .text{display:inline-block}.cxone-checkbox .icon svg{vertical-align:middle}.cxone-checkbox label{vertical-align:middle;font-weight:400}.cxone-checkbox [slot=image]{margin-right:10px;width:20px;height:20px}.cxone-checkbox input[type=checkbox]{opacity:0;vertical-align:middle;position:absolute}.cxone-checkbox input[type=checkbox]:focus-visible+label:before{border:1px solid rgb(0,124,190)}.cxone-checkbox input[type=checkbox]+label{font-size:13px}.cxone-checkbox input[type=checkbox]:indeterminate+label:after{content:\"\";opacity:1;top:8px;left:5px;width:6px;height:6px;background:#007cbe;-webkit-transform:rotate(0deg);-moz-transform:rotate(0deg);-o-transform:rotate(0deg);-ms-transform:rotate(0deg);transform:rotate(0)}.cxone-checkbox input[type=checkbox]+label:after{content:none}.cxone-checkbox input[type=checkbox]:checked+label:after{content:\"\"}.cxone-checkbox[disabled=true] input+label{color:#adadad}.cxone-checkbox[disabled=true] input+label:before{background:#f8f8f8 0 0 no-repeat padding-box;border:1px solid #526B7A}.cxone-checkbox[disabled=true] input+label:hover:before{border:1px solid #526B7A}.cxone-checkbox[disabled=true] input+label:after{color:#526b7a;opacity:.8}.cxone-checkbox[readonly=true] .check-wrapper{display:inline-block;width:16px}.cxone-checkbox[readonly=true] input:checked+label{font-family:OpenSans;font-weight:600}.cxone-checkbox[readonly=true] input:checked+label .check{display:inline-block;position:relative;top:-2px;transform:rotate(45deg);height:10px;width:5px;border-bottom:1.5px solid rgb(0,124,190);border-right:1.5px solid rgb(0,124,190);margin-right:10px}.cxone-checkbox[readonly=true] input:not(checked)+label{font-family:OpenSans}.cxone-checkbox:not([readonly]) label{position:relative;display:inline-block;color:#2e2e2e;padding-left:26px}.cxone-checkbox:not([readonly]) label:before,.cxone-checkbox:not([readonly]) label:after{position:absolute;content:\"\";display:inline-block}.cxone-checkbox:not([readonly]) label:hover:before{border:1px solid rgb(0,124,190)}.cxone-checkbox:not([readonly]) label:before{height:16px;width:16px;background:#fff 0 0 no-repeat padding-box;border:1px solid #526B7A;opacity:.8;left:0;top:3px}.cxone-checkbox:not([readonly]) label:after{height:5px;width:10px;border-left:1.5px solid;border-bottom:1.5px solid;display:inline-block;transform:rotate(-45deg);color:#007cbe;left:3px;top:8px}\n"]
    }]
  }], () => [{
    type: i0.Renderer2
  }], {
    checkboxId: [{
      type: Input
    }],
    disabled: [{
      type: Input
    }],
    indeterminate: [{
      type: Input
    }],
    labelText: [{
      type: Input
    }],
    name: [{
      type: Input
    }],
    ariaLabel: [{
      type: Input
    }],
    readonly: [{
      type: Input
    }],
    value: [{
      type: Input
    }],
    clicked: [{
      type: Output
    }],
    indeterminateChange: [{
      type: Output
    }],
    inputElement: [{
      type: ViewChild,
      args: ['inputElement', {
        static: true
      }]
    }]
  });
})();
class CheckboxModule {
  /** @nocollapse */static {
    this.ɵfac = function CheckboxModule_Factory(t) {
      return new (t || CheckboxModule)();
    };
  }
  /** @nocollapse */
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: CheckboxModule
    });
  }
  /** @nocollapse */
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, FormsModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CheckboxModule, [{
    type: NgModule,
    args: [{
      declarations: [CheckboxComponent],
      imports: [CommonModule, FormsModule],
      exports: [CheckboxComponent]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { CheckboxComponent, CheckboxModule };
